import React, { createContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, matchPath, useLocation, useNavigate, useParams } from 'react-router';
import classNames from 'clsx';
import { CloseRounded } from '@mui/icons-material';

import { DESIGN_INLINE_VERTICAL, DESIGN_VERTICAL } from '@/modules/ChannelList/constants/carousels';
import { DEFAULT_VIEW_PATH } from '@/modules/common/constants/routePath';

import { throttle } from '../../helpers/events';
import * as commonSelectors from '../../redux/selectors/common';
import { useTranslate } from '@/modules/common/helpers/i18n';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import { Dialog, IconButton } from '@/mui/components';

import styles from './PagePopup.module.scss';

type Props = {
  children: React.ReactNode;
};

export const IsInPopupContext = createContext(false);

function PagePopup({ children }: Props) {
  const open = true;
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ channel: string }>();
  const t = useTranslate();
  const channels = useSelector(commonSelectors.channelsSelector);
  const design = matchPath({ path: DEFAULT_VIEW_PATH }, location.pathname)
    ? channels.find((channel) => channel.id === Number(params.channel))?.design
    : undefined;

  const isVertical = [DESIGN_VERTICAL, DESIGN_INLINE_VERTICAL].includes(design as string);

  const throttledScrollEvent = useMemo(() => throttle(() => window.dispatchEvent(new Event('scroll')), 500), []);

  useEffect(
    () => () => {
      throttledScrollEvent.clear?.();
    },
    [throttledScrollEvent],
  );

  return (
    <Dialog
      classes={{
        paper: classNames(styles.Paper, {
          [styles.Paper___mobile]: isMobileUserAgent,
          [styles.Paper___blurred]: isVertical,
        }),
      }}
      open={open}
      scroll="body"
      onScroll={throttledScrollEvent}
      fullScreen={isVertical}
      fullWidth={isVertical}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          // https://github.com/facebook/create-react-app/issues/9485#issuecomment-1356873687
          // Re-dispatch the event to ensure it reaches global listeners
          window.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: event.key,
              code: event.code,
              keyCode: event.keyCode,
              bubbles: true,
              cancelable: true,
              shiftKey: event.shiftKey,
              ctrlKey: event.ctrlKey,
              metaKey: event.metaKey,
              altKey: event.altKey,
            }),
          );
        }
      }}
      disableEscapeKeyDown={!isVertical}
      onClose={isVertical ? () => navigate('/') : undefined}
    >
      {!isVertical && (
        <IconButton
          component={RouterLink}
          to="/"
          aria-label={t.closePopup.text()}
          className={styles.ButtonClose}
          size="medium"
        >
          <CloseRounded />
        </IconButton>
      )}
      {children}
    </Dialog>
  );
}

export default PagePopup;
